import { useMemo } from 'react';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { roleTaxonomySelectionToAiSearchId } from '../../shared/selection-lists/utils/roleTaxonomy';
import { useUserPreferences } from '../../shared/user-preferences/hooks/useUserPreferences';
import {
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../../shared/user-preferences/types';
import { deriveEffectiveTaxonomiesForScope } from '../../shared/user-preferences/utils/derive-effective-taxonomies';
import { resolveGeographyPreference } from '../../shared/user-preferences/utils/geography-preference';
import type { GlobalAssistantEntityType } from './global-ai-assistant-api';

/**
 * Taxonomy used by the global assistant when resolving route filters via
 * `aiFilterSearchRequest`. Mirrors the string format
 * `getRoleTaxonomyIdForAiSearch` produces from a CustomDash filter map,
 * but sourced from user preferences — so the drawer (which lives outside
 * CustomDash) can call the AI filter endpoint without depending on the
 * target page's filter context.
 *
 * The taxonomy choice is a single GLOBAL preference, and
 * `useUserPreferences(scope)` merges the GLOBAL record under every scope —
 * so the per-scope reads below all surface the one global choice.
 * The per-entity lookup is kept because the adaptive-resolved
 * `effective*TaxonomySelection` residue is only meaningful for company
 * entities; for the others an 'adaptive' choice falls through to the
 * generic default here, mirroring `deriveEffectiveTaxonomiesForScope`.
 *
 * React constraint: the drawer doesn't know which entity the user will
 * click in advance, so we subscribe to all six scopes (COMPANY / INDUSTRY /
 * GEOGRAPHY / ROLE / INDIVIDUAL / GLOBAL) up-front and return a lookup. React-Query dedupes
 * under a single query key, so the hook calls collapse to one request.
 */
/**
 * Narrow shape: just the values sent to the AI filter search API. The
 * bridge's `publishActiveTaxonomy` and the store's `activeTaxonomyByEntity`
 * deal in this shape — they mirror the page's `activeFilterMap` without
 * knowledge of user-preference identity.
 */
export type AssistantTaxonomyPayload = {
  rolesTax: string;
  skillsTax: number;
  activitiesTax: number;
};

/**
 * Full shape including cache-key identity. Used wherever caching matters
 * (the prefetch module, the page-stream cache). The drawer sources this
 * entirely from saved prefs (`useGlobalAssistantTaxonomy`); the
 * bridge-published store value is consumed only by the adaptive
 * refinement pass (`waitForTaxonomyChange`), never by the request payload.
 */
export type AssistantTaxonomy = AssistantTaxonomyPayload & {
  /**
   * Cache-key identity for the role taxonomy. Equals the literal string
   * `'adaptive'` when the user's role preference is adaptive; otherwise
   * equals `rolesTax`. Using preference identity instead of the resolved
   * numeric stops the cache from growing an entry per transient adaptive
   * re-resolution (user's Digital Commerce → k10 partial → k50 full all
   * collapse into one key for the same prompt).
   */
  rolesKey: string;
  /** See `rolesKey` — same idea, for skill. */
  skillsKey: string;
  /** See `rolesKey` — same idea, for activity. */
  activitiesKey: string;
  /**
   * Geography taxonomy id (metro 1 / admin 2) from the user's GLOBAL
   * geography preference. When absent, the backend resolves metro.
   */
  geoTax?: number;
};

function extractTaxonomy(preferences: UserPreferences): AssistantTaxonomy {
  const taxonomies = preferences?.taxonomies;
  const roleIsAdaptive = taxonomies?.role === 'adaptive';
  const skillIsAdaptive = taxonomies?.skill === 'adaptive';
  const activityIsAdaptive = taxonomies?.activity === 'adaptive';

  // Role precedence — the user's SELECTED taxonomy wins:
  //   1. A concrete stored `role` preference (the same source
  //      `SyncInitialPreferencesToFilters` applies to pages).
  //   2. Only when the pref is 'adaptive' (or unset): the adaptive-resolved
  //      `effectiveRoleTaxonomySelection` — unchanged prior behavior.
  // Mirrors `useRoleTaxonomySelection`. Previously the effective value won
  // unconditionally, so stale adaptive residue in the stored blob (e.g.
  // `custom_role_rics_k50_31`) silently overrode a concrete selection and the
  // assistant resolved/answered under a different taxonomy than the user's
  // pages run under.
  const roleSelection: CustomRoleTaxonomySelection | undefined = (() => {
    const raw = taxonomies?.role;
    if (raw && typeof raw === 'object') {
      return raw as CustomRoleTaxonomySelection;
    }
    const effective = taxonomies?.effectiveRoleTaxonomySelection;
    if (effective && typeof effective === 'object') {
      return effective as CustomRoleTaxonomySelection;
    }
    return undefined;
  })();

  // Skill precedence — same idea, mirroring `useSkillTaxonomy`: the concrete
  // stored numeric pref wins; the adaptive-resolved effective number only
  // fills in for 'adaptive'/unset. 0 = server treats as default.
  const skillsTax: number = (() => {
    const raw = taxonomies?.skill;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return raw;
    }
    const effective = taxonomies?.effectiveSkillTaxonomySelection;
    if (typeof effective === 'number' && Number.isFinite(effective)) {
      return effective;
    }
    return 0;
  })();

  // Activity precedence — same idea, mirroring `useActivityTaxonomy`: the
  // concrete stored numeric pref wins; the adaptive-resolved effective number
  // only fills in for 'adaptive'/unset. 0 = server treats as default.
  const activitiesTax: number = (() => {
    const raw = taxonomies?.activity;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return raw;
    }
    const effective = taxonomies?.effectiveActivityTaxonomySelection;
    if (typeof effective === 'number' && Number.isFinite(effective)) {
      return effective;
    }
    return 0;
  })();

  const rolesTax = roleTaxonomySelectionToAiSearchId(roleSelection);
  return {
    rolesTax,
    skillsTax,
    activitiesTax,
    rolesKey: roleIsAdaptive ? 'adaptive' : rolesTax,
    skillsKey: skillIsAdaptive ? 'adaptive' : String(skillsTax),
    activitiesKey: activityIsAdaptive ? 'adaptive' : String(activitiesTax),
  };
}

/** Exported for unit tests only — not part of the module's public surface. */
export { extractTaxonomy as extractTaxonomyForTest };

/**
 * Returns a `(entityType) => AssistantTaxonomy` resolver. Callers invoke
 * it at the moment they know the entity (e.g. after the suggest response
 * identifies the resolved scope, or when the user clicks a page chip).
 *
 * The returned function is memoized on the underlying preference
 * objects, so callers can safely put it in an effect dep array without
 * causing churn on every render.
 */
export function useGlobalAssistantTaxonomy(): (
  entityType: GlobalAssistantEntityType
) => AssistantTaxonomy {
  const company = useUserPreferences(PreferenceScope.COMPANY);
  const industry = useUserPreferences(PreferenceScope.INDUSTRY);
  const geography = useUserPreferences(PreferenceScope.GEOGRAPHY);
  const role = useUserPreferences(PreferenceScope.ROLE);
  const individual = useUserPreferences(PreferenceScope.INDIVIDUAL);
  const global = useUserPreferences(PreferenceScope.GLOBAL);

  return useMemo(() => {
    // The GLOBAL taxonomies record can carry adaptive-resolved residue
    // (effective*TaxonomySelection persisted from company pages); deriving
    // per scope keeps that residue from leaking into non-company payloads —
    // there an 'adaptive' choice must materialize as the generic default.
    const withDerivedTaxonomies = (
      prefs: UserPreferences,
      scope: PreferenceScope
    ): UserPreferences => ({
      ...prefs,
      taxonomies:
        deriveEffectiveTaxonomiesForScope(prefs?.taxonomies, scope) ??
        prefs?.taxonomies,
    });
    // The geography taxonomy is a single GLOBAL preference — one value for
    // every entityType — so it's derived once here and spread into each entry
    // rather than read per scope.
    const geoTaxonomy: Pick<AssistantTaxonomy, 'geoTax'> = {
      geoTax: resolveGeographyPreference(
        global.preferences?.[UserPreferenceKey.Geography]
      ).taxonomyId,
    };
    const nonCompanyEntityTaxonomy: AssistantTaxonomy = {
      ...extractTaxonomy(
        withDerivedTaxonomies(role.preferences, PreferenceScope.ROLE)
      ),
      ...geoTaxonomy,
    };
    const byEntity: Record<GlobalAssistantEntityType, AssistantTaxonomy> = {
      company: { ...extractTaxonomy(company.preferences), ...geoTaxonomy },
      industry: {
        ...extractTaxonomy(
          withDerivedTaxonomies(industry.preferences, PreferenceScope.INDUSTRY)
        ),
        ...geoTaxonomy,
      },
      geography: {
        ...extractTaxonomy(
          withDerivedTaxonomies(
            geography.preferences,
            PreferenceScope.GEOGRAPHY
          )
        ),
        ...geoTaxonomy,
      },
      role: nonCompanyEntityTaxonomy,
      // The skill and activity scopes hold no taxonomy record of their own
      // (it is GLOBAL-only), so they derive exactly what role does.
      skill: nonCompanyEntityTaxonomy,
      activity: nonCompanyEntityTaxonomy,
      // Talent Discovery stores its taxonomy under INDIVIDUAL — resolving a
      // prompt under any other scope would return a different role, since role
      // ids are taxonomy-relative. Derived like the others: adaptive only
      // anchors on COMPANY, so on TD it must materialize as generic.
      talentDiscovery: {
        ...extractTaxonomy(
          withDerivedTaxonomies(
            individual.preferences,
            PreferenceScope.INDIVIDUAL
          )
        ),
        ...geoTaxonomy,
      },
      // Posting Discovery declares no primary filter, so `getPreferenceScope`
      // resolves it to GLOBAL — NOT INDIVIDUAL like Talent Discovery. Reading
      // any other scope here would resolve roles under a different taxonomy
      // than the page then queries with, and the ids would come back empty.
      postingDiscovery: {
        ...extractTaxonomy(
          withDerivedTaxonomies(global.preferences, PreferenceScope.GLOBAL)
        ),
        ...geoTaxonomy,
      },
    };
    return (entityType) => byEntity[entityType];
  }, [
    company.preferences,
    industry.preferences,
    geography.preferences,
    role.preferences,
    individual.preferences,
    global.preferences,
  ]);
}

/**
 * True once every preference scope the resolver reads has hydrated (server response or
 * confirmed fallback to local/session values). Until then the resolver above
 * serves DEFAULTS for a cold session — an assistant turn fired that early
 * sends a default/empty `rolesTax`, and the backend resolves roles under the
 * default taxonomy (the "Senior Data Scientist → Data Strategy → empty
 * chart" failure). The engine holds turn starts on this flag, briefly: an
 * instant example-prompt click on the assistant homepage is exactly the
 * cold-session race the drawer's slower open-then-type flow used to mask.
 */
export function useGlobalAssistantTaxonomyReady(): boolean {
  const company = useUserPreferences(PreferenceScope.COMPANY);
  const industry = useUserPreferences(PreferenceScope.INDUSTRY);
  const geography = useUserPreferences(PreferenceScope.GEOGRAPHY);
  const role = useUserPreferences(PreferenceScope.ROLE);
  const individual = useUserPreferences(PreferenceScope.INDIVIDUAL);
  const global = useUserPreferences(PreferenceScope.GLOBAL);
  return (
    !company.isInitialLoading &&
    !industry.isInitialLoading &&
    !geography.isInitialLoading &&
    !role.isInitialLoading &&
    !individual.isInitialLoading &&
    !global.isInitialLoading
  );
}
